import { getUniqueId, parseNumberStrict, removeNulls, safeParseNullDefault } from 'shouldve-been-js';
import AppConfig from '../Models/AppConfigModel';
import BugReport from '../Models/BugReportModel';
import Category from '../Models/CategoryModel';
import ChecklistModel from '../Models/Checklists/ChecklistModelModel';
import type FilledChecklistValueObject from '../Models/Checklists/FilledChecklistValueObjectModel';
import Country from '../Models/CountryModel';
import CurrentUser from '../Models/CurrentUserModel';
import Department from '../Models/DepartmentModel';
import DocumentNode from '../Models/DocumentNodeModel';
import DocumentTreeNode from '../Models/DocumentTreeNodeModel';
import InspectingCompany from '../Models/InspectingCompanyModel';
import Inspector from '../Models/InspectorModel';
import KFleetToken from '../Models/KFleetTokenModel';
import SystemInfo from '../Models/SystemInfoModel';
import Tenant from '../Models/TenantModel';
import TenantToken from '../Models/TenantTokenModel';
import UserDepartmentLink from '../Models/UserDepartmentLinkModel';
import User from '../Models/UserModel';
import { datetimeToApiString } from '../Utils/DateTimeParser';
import type IndexedDBService from './IndexedDBService';
import MemoTemplate from '../Models/MemoTemplateModel';
import ObservationModel from '../Models/ObservationModelModel';
import type FilledObservationLine from '../Models/FilledObservationLineModel';
import ObservationChapter from '../Models/ObservationChapterModel';
import ObservationSubChapter from '../Models/ObservationSubChapterModel';
import ObservationRuleSet from '../Models/ObservationRuleSetModel';

class ApiService {

    private _baseUrl: string;

    public constructor(baseUrl: string) {
        this._baseUrl = baseUrl;
    }

    public get baseUrl(): string {
        return this._baseUrl;
    }

    public set baseUrl(value: string) {
        this._baseUrl = value;
    }

    public copy(): ApiService {
        const newApi: ApiService = new ApiService(this.baseUrl);
        return newApi;
    }

    public async getHealth(token: string | null): Promise<boolean> {

        const endpointUrl: string = '/int-v1/system/health';

        try {
            const response: Response = token === null //endpoint only needs a token if used through the tunnel
                ? await fetch(
                    this.baseUrl + endpointUrl, 
                    { 
                        mode: 'cors',
                        method: 'GET',
                    },
                )
                : await fetch(
                    this.baseUrl + endpointUrl, 
                    { 
                        mode: 'cors',
                        method: 'GET',
                        headers: {
                            Authorization: 'Bearer ' + token,
                        },
                    },
                );

            if (response.status >= 400) {

                return false;
            }

            return true;       
        } catch (e: unknown) {
            return false;  
        }
    }

    public async getSystemInfo(token: string | null): Promise<SystemInfo | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            return SystemInfo.fromJson(jsonResponse);
            
        } catch (e: unknown) {
            return null;  
        }
    }

    public async getCompanyLogo(token: string | null): Promise<Blob | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/logo?module=inventory&format=png&transparentPng=true';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            return await response.blob();

        } catch (e: unknown) {
            return null;  
        }
    }

    public async getAppConfig(token: string | null): Promise<AppConfig | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/appconfig/mobile/experience';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            return AppConfig.fromJson(jsonResponse);

        } catch (e: unknown) {
            return null;  
        }
    }

    public async fetchNewKFleetTokenByUsernameAndPassword(username: string, password: string): Promise<KFleetToken | null> {

        const tokenUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + tokenUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: username,
                        password: password,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const tokenInfo: KFleetToken = KFleetToken.fromJson(jsonResponse);

            return tokenInfo;

        } catch (e: unknown) {
            return null;
        }
    }

    public static async fetchNewTenantTokenByEmailAndPassword(email: string, password: string, tenantDisplayName: string): Promise<TenantToken | null> {

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/credentials/login',
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                        tenantDisplayName: tenantDisplayName,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const tokenInfo: TenantToken = TenantToken.fromJson(jsonResponse);

            return tokenInfo;

        } catch (e: unknown) {
            return null;
        }
    }

    public async fetchNewKFleetTokenByRefreshToken(refreshToken: string, indexedDBService: IndexedDBService): Promise<KFleetToken | null | undefined> { //null means refresh token has expired, while undefined could simply mean you are offline

        const tokenUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + tokenUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        refreshtoken: refreshToken,
                    }),
                },
            );

            if (response.status === 401) { //refresh token is already used or expired
                await indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'Refresh token is already used or expired (K-Fleet)'));
                return null;
            }

            if (response.status >= 400) { //if failed to fetch token
                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();
            
            const tokenInfo: KFleetToken = KFleetToken.fromJson(jsonResponse);

            await indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'New token has been fetched from refresh token (K-Fleet)'));

            return tokenInfo;

        } catch (e: unknown) {
            return undefined;
        }
    }

    public static async fetchNewTenantTokenByRefreshToken(refreshToken: string, indexedDBService: IndexedDBService): Promise<TenantToken | null | undefined> { //null means refresh token has expired, while undefined could simply mean you are offline

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/credentials/refreshtoken',
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        refresh_token: refreshToken,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token

                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const textResponse: string = await response.text();

                if (textResponse === 'INVALID_REFRESH_TOKEN\n') { //refresh token is already used or expired
                    await indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'Refresh token is already used or expired (tenant)'));
                    return null;
                }

                return undefined;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();
            
            const tokenInfo: TenantToken = TenantToken.fromJson(jsonResponse);

            await indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Token', 'New token has been fetched from refresh token (tenant)'));

            return tokenInfo;

        } catch (e: unknown) {
            return undefined;
        }
    }

    public static async getTenantsByEmail(email: string): Promise<Tenant[]> {

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/tenants/user-tenants/' + email,
                { 
                    mode: 'cors',
                    method: 'GET',
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const tenants: (Tenant | null)[] = jsonResponse?.map((jsonObject: any) => {
                return Tenant.fromJson(jsonObject);
            });

            return removeNulls(tenants);  

        } catch (e: unknown) {
            return [];
        }
    }

    public async logout(token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        } catch (e: unknown) {
            return false;
        }
    }

    public async getUserAccess(userId: string, token: string | null): Promise<string[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/users/' + userId + '/permissions';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
            const permissions: (string | null)[] = jsonResponse.items.map((item: any) => safeParseNullDefault<string>(item?.accessId, ''));

            return removeNulls(permissions);

        } catch (e: unknown) {
            return [];
        }
    }

    public async getCurrentUserByEmail(email: string, token: string | null): Promise<CurrentUser | null> {

        if (token === null) {
            return null;
        }

        const endpointUrl: string = '/int-v1/users?loadPermissions=true&$top=1&$filter=email eq \'' + email + '\'';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse.items)) {
                return null;
            }

            if (jsonResponse.items.length === 0) {
                return null;
            }

            return CurrentUser.fromJson(jsonResponse.items[0]);

        } catch (e: unknown) {
            return null;
        }
    }

    public async getServerTimezoneOffsetMinutes(token: string | null): Promise<number | null> {

        if (token === null) { //endpoint strictly only requires a token if used through a tunnel, but as a token should always be available when getting this, the check is here
            return null;
        }

        const endpointUrl: string = '/int-v1/system/time';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const localTime: string | null = safeParseNullDefault<string>(jsonResponse?.localTime, '');

            if (localTime === null) {
                return null;
            }

            if (localTime.includes('+')) {

                const timezoneString: string = localTime.split('+')[1] ?? '';

                if (timezoneString.length !== 5 || timezoneString[2] !== ':') {
                    return null;
                }

                const hourPart: number | null = parseNumberStrict(timezoneString.substring(0, 2));
                const minutePart: number | null = parseNumberStrict(timezoneString.substring(3, 5));

                if (hourPart === null || minutePart === null) {
                    return null;
                }

                return hourPart * 60 + minutePart;

            } else if (localTime.includes('-')) {

                const timezoneString: string = localTime.split('-')[1] ?? '';

                if (timezoneString.length !== 5 || timezoneString[2] !== ':') {
                    return null;
                }

                const hourPart: number | null = parseNumberStrict(timezoneString.substring(0, 2));
                const minutePart: number | null = parseNumberStrict(timezoneString.substring(3, 5));

                if (hourPart === null || minutePart === null) {
                    return null;
                }

                return -(hourPart * 60 + minutePart);

            } else {
                return 0;
            }
            
        } catch (e: unknown) {
            return null;  
        }
    }

    public async saveImageItem(name: string, fileName: string | null, createdDate: Date, imageBlob: Blob | null, folderId: string | null, serverTimezoneOffsetMinutes: number, token: string | null): Promise<boolean> {

        if (token === null || imageBlob === null || fileName === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/links/document';

        const formData: FormData = new FormData();
        formData.append('file', imageBlob);
        formData.append('metadata', JSON.stringify({
            linkType: 'DOC',
            origin: 'FILE',
            filename: fileName,
            description: name,
            liveLink: false,
            source: 'EX_OFF_PWA',
            fileCategory: 'IMAGE',
            linkDate: datetimeToApiString(createdDate, serverTimezoneOffsetMinutes),
            folderId: folderId,
            status: 'T',
        }));

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    body: formData,
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return false;  
        }
    }

    public async saveCommentItem(name: string, createdDate: Date, comment: string, folderId: string | null, serverTimezoneOffsetMinutes: number, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/experience/comments';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        title: name,
                        createdDate: datetimeToApiString(createdDate, serverTimezoneOffsetMinutes),
                        content: comment,
                        folderId: folderId,
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return false;  
        }
    }

    public async getAllUsers(token: string | null): Promise<User[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/users?$top=20000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const users: (User | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return User.fromJson(jsonObject);
            });

            return removeNulls(users);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getCountries(token: string | null): Promise<Country[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/system/countries?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const countries: (Country | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return Country.fromJson(jsonObject);
            });

            return removeNulls(countries);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getAllChecklistModels(token: string | null): Promise<ChecklistModel[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/checklists/models';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const checklistModels: (ChecklistModel | null)[] = Object.entries(jsonResponse).map(([key, value]: [string, any]) => {
                return ChecklistModel.fromJson(key, value);
            });

            return removeNulls(checklistModels);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async saveFilledChecklist(checklistId: string, name: string, checklistModel: ChecklistModel, values: FilledChecklistValueObject[], folderId: string, sortIndex: number, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
        const jsonValueObjects: any[] = values.map((value: FilledChecklistValueObject) => value.toJson(checklistModel));

        const endpointUrl: string = '/int-v1/experience/checklists/filled';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        checklistId: checklistId,
                        caption: name,
                        values: removeNulls(jsonValueObjects),
                        folderId: folderId,
                        sortIndex: sortIndex - 1, //the sortIndeces are initially 1-indexed, but should be 0-indexed before being saved, so that is fixed here
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return false;  
        }
    }

    public async getInspectingCompanies(token: string | null): Promise<InspectingCompany[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/inspectingcompanies?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const inspectingCompanies: (InspectingCompany | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return InspectingCompany.fromJson(jsonObject);
            });

            return removeNulls(inspectingCompanies);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getInspectors(token: string | null): Promise<Inspector[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/inspectors?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const inspectors: (Inspector | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return Inspector.fromJson(jsonObject);
            });

            return removeNulls(inspectors);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getDepartments(token: string | null): Promise<Department[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/departments?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const departments: (Department | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return Department.fromJson(jsonObject);
            });

            return removeNulls(departments);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getDepartmentsForAllUsersAndRoles(token: string | null): Promise<UserDepartmentLink[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/departments/users';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const userDepartmentLinks: (UserDepartmentLink | null)[] = Object.entries(jsonResponse).map(([key, value]: [string, any]) => {

                return UserDepartmentLink.fromJson(key, value);
            });

            return removeNulls(userDepartmentLinks);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return []; 
        }
    }

    public async getCategories(token: string | null): Promise<Category[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/categories?$top=10000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const categories: (Category | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return Category.fromJson(jsonObject);
            });

            return removeNulls(categories);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getDocumentTreeNodes(token: string | null): Promise<DocumentTreeNode[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/documents/nodes?$top=10000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const treeNodes: (DocumentTreeNode | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return DocumentTreeNode.fromJson(jsonObject);
            });

            const visibleTreeNodes: DocumentTreeNode[] = DocumentTreeNode.filterAvailableNodes(removeNulls(treeNodes));

            return visibleTreeNodes;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getDocumentNodes(token: string | null): Promise<DocumentNode[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/documents?$top=10000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const documentNodes: (DocumentNode | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return DocumentNode.fromJson(jsonObject);
            });

            return removeNulls(documentNodes);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    // eslint-disable-next-line class-methods-use-this
    public async getDocument(documentId: string, sortNumber: number, token: string | null): Promise<Blob | null> {

        return null; //TODO: temporarily until there is a restriction on what documents to fetch

        /*if (token === null) {
            return null;
        }

        const endpointUrl: string = '/int-v1/experience/documents/' + documentId + '/' + sortNumber.toString();

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return null;
            }

            return await response.blob();

        } catch (e: unknown) {
            return null;  
        }*/
    }

    public async getMemoTemplates(token: string | null): Promise<MemoTemplate[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/memos/templates';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const memoTemplates: (MemoTemplate | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return MemoTemplate.fromJson(jsonObject);
            });

            return removeNulls(memoTemplates);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async saveFolder(folderId: string, templateId: string | null, name: string, createdDate: Date, serverTimezoneOffsetMinutes: number, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/experience/folders';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'PUT',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        folderId: folderId,
                        origin: templateId === null ? null : 'MEM',
                        objectId: templateId,
                        name: name,
                        createdDate: datetimeToApiString(createdDate, serverTimezoneOffsetMinutes),
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return false;  
        }
    }

    public async getAllObservationModels(token: string | null): Promise<ObservationModel[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/observations/models';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const observationModels: (ObservationModel | null)[] = Object.entries(jsonResponse).map(([key, value]: [string, any]) => {
                return ObservationModel.fromJson(key, value);
            });

            return removeNulls(observationModels);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async saveFilledObservation(observationId: string, name: string, ruleSetId: string, observationLines: FilledObservationLine[], folderId: string, sortIndex: number, serverTimezoneOffsetMinutes: number, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
        const jsonObservationLineObjects: any[] = observationLines.map((observationLine: FilledObservationLine) => observationLine.toJson(serverTimezoneOffsetMinutes));

        const endpointUrl: string = '/int-v1/experience/observations/filled';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        observationId: observationId,
                        caption: name,
                        ruleSetId: ruleSetId,
                        observationLines: jsonObservationLineObjects,
                        folderId: folderId,
                        sortIndex: sortIndex - 1, //the sortIndeces are initially 1-indexed, but should be 0-indexed before being saved, so that is fixed here
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return false;  
        }
    }

    public async getObservationChapters(token: string | null): Promise<ObservationChapter[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/observations/chapters?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const chapters: (ObservationChapter | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return ObservationChapter.fromJson(jsonObject);
            });

            return removeNulls(chapters);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getObservationSubChapters(token: string | null): Promise<ObservationSubChapter[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/observations/subchapters?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const subChapters: (ObservationSubChapter | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return ObservationSubChapter.fromJson(jsonObject);
            });

            return removeNulls(subChapters);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async getObservationRuleSets(token: string | null): Promise<ObservationRuleSet[]> {

        if (token === null) {
            return [];
        }

        const endpointUrl: string = '/int-v1/experience/observations/rulesets?$top=1000';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse?.items)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const ruleSets: (ObservationRuleSet | null)[] = jsonResponse?.items?.map((jsonObject: any) => {
                return ObservationRuleSet.fromJson(jsonObject);
            });

            return removeNulls(ruleSets);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            return [];  
        }
    }

    public async sendBugReport(bugReport: string, description: string, token: string | null): Promise<boolean> {

        if (token === null) {
            return false;
        }

        const endpointUrl: string = '/int-v1/system/errorlog/mobile';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        app: 'experience',
                        log: bugReport,
                        description: description,
                    }),
                },
            ); 
            
            if (response.status >= 400) {
                return false;
            }

            return true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return false;  
        }
    }
}

export default ApiService;